;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import type { SalaryFrequency, SalaryType } from '@workspace/ui/types';
import { SALARY_FREQUENCY_OPTIONS, SALARY_TYPE_OPTIONS } from './constants';

const MONTHS_PER_YEAR = 12;

type SalaryFieldValue = string | number | null | undefined;

interface SalaryFields {
  grossSalary?: SalaryFieldValue;
  netSalary?: SalaryFieldValue;
  salaryType?: SalaryType | null;
}

interface EffectiveSalary {
  amount: number | null;
  salaryType: SalaryType;
  grossSalary: number | null;
  netSalary: number | null;
}

/**
 * Normalizes a single salary field from the API. Treats null, undefined, empty string,
 * 0, NaN, and non-positive numbers as unset — the BE may return 0 instead of null for
 * the unused gross/net field on a seat.
 */
export function parseSalaryValue(value: SalaryFieldValue): number | null {
  if (value === null || value === undefined || value === '') return null;

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Routes a single entered salary amount into the gross_salary / net_salary fields the
 * /seats endpoints expect, based on the country's salary type. The unused field is set to
 * null so PUT/PATCH can clear any stale value server-side.
 */
export function splitSalaryByType(
  salaryType: SalaryType,
  amount: number | null
): { gross_salary: number | null; net_salary: number | null } {
  return salaryType === SALARY_TYPE_OPTIONS.NET
    ? { gross_salary: null, net_salary: amount }
    : { gross_salary: amount, net_salary: null };
}

/** Legal monthly minimum for the country's salary type (gross or net). */
export function getMinMonthlySalary(
  employment:
    | {
        min_monthly_gross_salary: number;
        min_monthly_net_salary: number;
      }
    | undefined,
  salaryType: SalaryType | null | undefined
): number | undefined {
  if (!employment) return undefined;

  return salaryType === SALARY_TYPE_OPTIONS.NET
    ? employment.min_monthly_net_salary
    : employment.min_monthly_gross_salary;
}

/** Converts an entered salary to its monthly figure for validation and country-field lookups. */
export function getMonthlySalaryAmount(
  amount: number,
  salaryFrequency: SalaryFrequency = SALARY_FREQUENCY_OPTIONS.MONTHLY
): number {
  return salaryFrequency === SALARY_FREQUENCY_OPTIONS.ANNUAL ? amount / MONTHS_PER_YEAR : amount;
}

/** Returns the opposite-frequency equivalent of an entered salary (monthly × 12 or annual ÷ 12). */
export function getEquivalentSalaryAmount(
  amount: number,
  salaryFrequency: SalaryFrequency
): number {
  return salaryFrequency === SALARY_FREQUENCY_OPTIONS.ANNUAL
    ? amount / MONTHS_PER_YEAR
    : amount * MONTHS_PER_YEAR;
}

/** Builds the salary fields POST /seats expects, including the input-only frequency hint. */
export function buildSeatSalaryPayload(
  salaryType: SalaryType,
  amount: number | null,
  salaryFrequency?: SalaryFrequency | null
): {
  gross_salary: number | null;
  net_salary: number | null;
  salary_frequency: SalaryFrequency;
} {
  return {
    ...splitSalaryByType(salaryType, amount),
    salary_frequency: salaryFrequency ?? SALARY_FREQUENCY_OPTIONS.MONTHLY,
  };
}

/**
 * Parses seat salary fields, detects gross vs net from whichever field is set, and returns
 * the amount to display or use in calculations. Only one of grossSalary / netSalary should
 * be set on a seat; when both are empty, salaryType is used as a fallback label only.
 */
export function getEffectiveSalary(input: SalaryFields): EffectiveSalary {
  const grossSalary = parseSalaryValue(input.grossSalary);
  const netSalary = parseSalaryValue(input.netSalary);

  if (netSalary !== null) {
    return {
      amount: netSalary,
      salaryType: SALARY_TYPE_OPTIONS.NET,
      grossSalary: null,
      netSalary,
    };
  }

  if (grossSalary !== null) {
    return {
      amount: grossSalary,
      salaryType: SALARY_TYPE_OPTIONS.GROSS,
      grossSalary,
      netSalary: null,
    };
  }

  return {
    amount: null,
    salaryType: input.salaryType ?? SALARY_TYPE_OPTIONS.GROSS,
    grossSalary,
    netSalary,
  };
}

interface ResolveSalaryChangeValuesInput {
  grossSalary?: SalaryFieldValue;
  netSalary?: SalaryFieldValue;
  amountType?: string | null;
  salary?: SalaryFieldValue;
  salaryNew?: SalaryFieldValue;
}

function isSalaryType(value: string | null | undefined): value is SalaryType {
  return value === SALARY_TYPE_OPTIONS.NET || value === SALARY_TYPE_OPTIONS.GROSS;
}

/** Resolves gross vs net for salary-change monthly inputs (create, edit, and display). */
export function resolveSalaryChangeAmountType(
  input: Pick<ResolveSalaryChangeValuesInput, 'grossSalary' | 'netSalary' | 'amountType'>
): SalaryType {
  if (isSalaryType(input.amountType)) {
    return input.amountType;
  }

  return getEffectiveSalary({
    grossSalary: input.grossSalary,
    netSalary: input.netSalary,
  }).salaryType;
}

/** Resolves current/new salary values and gross-vs-net labels for salary-change inputs. */
export function resolveSalaryChangeValues(input: ResolveSalaryChangeValuesInput): {
  amountType: SalaryType;
  currentSalary: string;
  newSalary: string;
  isNetSalary: boolean;
} {
  const amountType = resolveSalaryChangeAmountType(input);
  const isNetSalary = amountType === SALARY_TYPE_OPTIONS.NET;

  const fromRecord = parseSalaryValue(input.salary);
  const fromRecordNew = parseSalaryValue(input.salaryNew);
  const { amount: employmentAmount } = getEffectiveSalary({
    grossSalary: input.grossSalary,
    netSalary: input.netSalary,
  });

  const currentSalary = fromRecord ?? employmentAmount;
  const newSalary = fromRecordNew;

  return {
    amountType,
    currentSalary: currentSalary != null ? String(currentSalary) : '',
    newSalary: newSalary != null ? String(newSalary) : '',
    isNetSalary,
  };
}

/** Builds the salary-change payload for monthly-input create/update requests. */
export function buildSalaryChangeMonthlyInputPayload(
  currentSalaryValue: string | number,
  newSalaryValue: string | number,
  amountType: SalaryType
): { salary: number; salary_new: number; amount_type: SalaryType } {
  const salary = parseSalaryValue(currentSalaryValue);
  const salaryNew = parseSalaryValue(newSalaryValue);

  if (salary == null) {
    throw new Error('Current salary value is required');
  }

  if (salaryNew == null) {
    throw new Error('New salary value is required');
  }

  return {
    salary,
    salary_new: salaryNew,
    amount_type: amountType,
  };
}

/** Returns the new salary amount from a salary-change record for display. */
export function getSalaryChangeNewAmount(input: { salary_new?: SalaryFieldValue }): string | null {
  const amount = parseSalaryValue(input.salary_new);
  return amount != null ? String(amount) : null;
}

function getStartOfMonth(date: Date): Date {
  const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
  monthStart.setHours(0, 0, 0, 0);
  return monthStart;
}

/** True when `date` falls in a calendar month before `monthReference`. */
export function isDateBeforeMonth(date: Date, monthReference: Date): boolean {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate < getStartOfMonth(monthReference);
}
